import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { Button, Notice } from '../../design-system/components';
import { GuidedConstruction } from './GuidedConstruction';
import { ContentEditor } from './ContentEditor';
import { FinalizeProposal } from './Preview';
import { serviceCustomerName } from './helpers';
import type { Bootstrap, Proposal, Stage } from '../../types/contracts';
const stages: [Stage, string][] = [
  ['construir', 'Construir'],
  ['editar', 'Editar conteúdo'],
  ['finalizar', 'Finalizar proposta'],
];
export function ProposalWorkspace({
  id,
  bootstrap,
  onOpen,
  onManage,
}: {
  id: string;
  bootstrap: Bootstrap;
  onOpen: (id: string) => void;
  onManage: () => void;
}) {
  const [p, setP] = useState<Proposal | null>(null);
  const [error, setError] = useState('');
  const [openFinancialOnReturn, setOpenFinancialOnReturn] = useState(false);
  async function load() {
    try {
      setP(await api.get(`/proposals/${id}`));
    } catch (e: any) {
      setError(e.message);
    }
  }
  useEffect(() => {
    load();
  }, [id]);
  useEffect(() => {
    if (!p) return;
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
  }, [id, p?.construction_stage, p?.immutable]);
  async function stage(s: Stage) {
    try {
      await api.put(`/proposals/${id}/stage`, { stage: s });
      setP((old) => (old ? { ...old, construction_stage: s } : old));
    } catch (e: any) {
      setError(e.message);
    }
  }
  if (!p) return <main className="page">Carregando proposta...</main>;
  const current = p.immutable ? 'finalizar' : p.construction_stage || 'construir';
  const service = bootstrap.services[p.service_code];
  return (
    <main className="workspace">
      <header className="workspace-header">
        <div>
          <span className="eyebrow">
            {p.code} · REV {String(p.revision).padStart(2, '0')}
          </span>
          <h1>{p.object_text}</h1>
          <p>{serviceCustomerName(p.service_code, service)}</p>
        </div>
        <div className="workspace-actions">
          <span className={`state-pill ${p.immutable ? 'issued' : 'draft'}`}>{p.immutable ? 'Somente leitura' : 'Rascunho editável'}</span>
          <Button variant="ghost" onClick={onManage}>
            Gerenciar proposta
          </Button>
        </div>
      </header>
      {error && (
        <Notice tone="danger" title="Ação não concluída">
          {error}
        </Notice>
      )}
      <nav className="journey-stepper" aria-label="Etapas da proposta">
        {stages.map(([key, label], i) => {
          const index = stages.findIndex(([s]) => s === current);
          return (
            <button
              key={key}
              className={`${key === current ? 'current' : ''} ${i < index ? 'complete' : ''}`}
              onClick={() => !p.immutable && stage(key)}
              disabled={p.immutable && key !== 'finalizar'}
            >
              <span>{i < index ? '✓' : i + 1}</span>
              {label}
            </button>
          );
        })}
      </nav>
      {current === 'construir' && !p.immutable && (
        <GuidedConstruction
          id={id}
          openFinancialInitially={openFinancialOnReturn}
          onFinancialInitialOpenHandled={() => setOpenFinancialOnReturn(false)}
          onComplete={() => {
            stage('editar');
            load();
          }}
        />
      )}
      {current === 'editar' && !p.immutable && (
        <ContentEditor
          id={id}
          onBack={() => stage('construir')}
          onEditPrice={() => {
            setOpenFinancialOnReturn(true);
            stage('construir');
          }}
          onFinalize={() => {
            stage('finalizar');
            load();
          }}
        />
      )}
      {current === 'finalizar' && (
        <FinalizeProposal id={id} onBack={() => (p.immutable ? undefined : stage('editar'))} onManage={onManage} onRevision={onOpen} />
      )}
    </main>
  );
}
